import _ from 'lodash';
import {
  EVIDENCE_CONTEXT_LIMIT,
  GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE_COMMENTS,
  GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE_LABELS,
  GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE_NUMERICS,
  GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE_OPAQUE,
  LONG_AGO,
  VantageNames,
} from '@/vantage/constants';
import type { Moment } from 'moment';
import { NUMBER_CONVERSIONS } from '@/main/app.constants';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import type { Filter } from '@/serverSideTable/materializedTables/materializedTables.constants';
import { GraphQL } from '@/serverSideTable/materializedTables/materializedTables.constants';
import {
  andFilters,
  equal,
  isNotNull,
  isNull,
  notEqual,
  orFilters,
  simpleFilter,
  valueFilter,
} from '@/serverSideTable/materializedTables/filters';
import { getTable } from '@/serverSideTable/materializedTables/queries';
import { sqContextApi } from '@/sdk/api/ContextApi';
import type { Filter as ContextFilter } from '@/sdk/model/Filter';
import type {
  AllContextOutputV1,
  CategoryLabelPairInputV1,
  ContextLabelListOutputV1,
  LabelHierarchyOutputV1,
} from 'sdk/model/models';

export async function context(itemId: string, datumId: string | undefined) {
  const {
    data: { opaque },
  } = await sqContextApi.getContextOpaque({ ids: [{ itemId, datumIds: datumId ? [datumId] : [] }] });

  return opaque;
}

async function fetchContextForIdPairs(
  idPairs: ([string, string] | [string])[],
  filters?: { labelFilter?: Filter; commentFilter?: Filter; opaqueFilter?: Filter; numericFilter?: Filter },
  cancellationGroup?: string,
): Promise<AllContextOutputV1> {
  if (idPairs.length === 0) {
    return { labels: [], comments: [], opaque: [], numerics: [] };
  }
  const ids = _.chain(idPairs)
    .groupBy((idPair) => idPair[0])
    .map((idPairs, itemId) => {
      const datumIds = idPairs.flatMap((idPair) => idPair[1]).filter((datumId) => datumId) as string[];
      return {
        itemId,
        datumIds: datumIds.length > 0 ? datumIds : undefined,
      };
    })
    .value();

  const { labelFilter, commentFilter, opaqueFilter, numericFilter } = filters ?? {};
  const { data } = await sqContextApi.getAllContext(
    {
      ids,
      labelFilter: labelFilter as unknown as ContextFilter,
      commentFilter: commentFilter as unknown as ContextFilter,
      opaqueFilter: opaqueFilter as unknown as ContextFilter,
      numericFilter: numericFilter as unknown as ContextFilter,
    },
    { cancellationGroup, cancelOnServer: true },
  );
  return data;
}

export async function allContextForIdPairs(
  idPairs: ([string, string] | [string])[],
  datumIds?: string[],
  cancellationGroup?: string,
): Promise<AllContextOutputV1> {
  const labelFilter = datumIds
    ? orFilters(
        simpleFilter(VantageNames.TableQueryContextColumns.ContextLabelsDatumId, GraphQL.FilterTypes.IsNull),
        valueFilter(VantageNames.TableQueryContextColumns.ContextLabelsDatumId, GraphQL.FilterTypes.In, datumIds),
      )
    : undefined;

  const commentFilter = datumIds
    ? andFilters(
        orFilters(
          simpleFilter(VantageNames.TableQueryContextColumns.ContextCommentsDatumId, GraphQL.FilterTypes.IsNull),
          valueFilter(VantageNames.TableQueryContextColumns.ContextCommentsDatumId, GraphQL.FilterTypes.In, datumIds),
        ),
        orFilters(
          // When category ID is null, we're fetching comment contexts
          isNull(VantageNames.TableQueryContextColumns.ContextCommentsCategoryId),
          // When category ID is not null, we're fetching text contexts but excluding archived ones
          equal(VantageNames.TableQueryContextColumns.ContextCommentsArchived, false),
        ),
      )
    : undefined;

  const opaqueFilter = datumIds
    ? orFilters(
        simpleFilter(VantageNames.TableQueryContextColumns.ContextOpaqueDatumId, GraphQL.FilterTypes.IsNull),
        valueFilter(VantageNames.TableQueryContextColumns.ContextOpaqueDatumId, GraphQL.FilterTypes.In, datumIds),
      )
    : undefined;

  const numericFilter = datumIds
    ? orFilters(
        simpleFilter(VantageNames.TableQueryContextColumns.ContextNumericDatumId, GraphQL.FilterTypes.IsNull),
        valueFilter(VantageNames.TableQueryContextColumns.ContextNumericDatumId, GraphQL.FilterTypes.In, datumIds),
      )
    : undefined;

  return await fetchContextForIdPairs(
    idPairs,
    { labelFilter, commentFilter, opaqueFilter, numericFilter },
    cancellationGroup,
  );
}

export async function labelContextForIdPairs(
  idPairs: ([string, string] | [string])[],
  filters?: { labelFilter?: Filter; commentFilter?: Filter; opaqueFilter?: Filter },
  cancellationGroup?: string,
): Promise<ContextLabelListOutputV1> {
  return fetchContextForIdPairs(idPairs, filters, cancellationGroup);
}

export function evidenceFilter(
  start: Moment,
  end: Moment,
  includeExtinct: boolean,
  includeArchived: boolean,
  lastFetched: Date = LONG_AGO,
) {
  return andFilters(
    valueFilter(
      VantageNames.Columns.Start,
      GraphQL.FilterTypes.LessThan,
      end.valueOf() * NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND,
    ),
    orFilters(
      isNull(VantageNames.Columns.End),
      valueFilter(
        VantageNames.Columns.End,
        GraphQL.FilterTypes.GreaterThan,
        start.valueOf() * NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND,
      ),
    ),
    valueFilter(VantageNames.Columns.UpdatedAt, GraphQL.FilterTypes.GreaterThanOrEqual, lastFetched.toISOString()),
    includeArchived ? null : isNull(VantageNames.Columns.DeletedAt),
    includeExtinct ? null : notEqual(VantageNames.Columns.LastCapsuleState, VantageNames.ColumnValues.Extinct),
  );
}

type EvidenceContextFilterParams = {
  start: Moment;
  end: Moment;
  lastFetched: Date;
  includeExtinct: boolean;
  updatedAtColumn: string;
  archivedColumn: string;
  categoryColumn?: string;
  includeArchived?: boolean;
  hasCategory?: boolean;
};

const evidenceContextFilter = ({
  start,
  end,
  lastFetched,
  includeExtinct,
  updatedAtColumn,
  archivedColumn,
  categoryColumn,
  includeArchived = false,
  hasCategory = false,
}: EvidenceContextFilterParams) =>
  andFilters(
    evidenceFilter(start, end, includeExtinct, includeArchived),
    valueFilter(updatedAtColumn, GraphQL.FilterTypes.GreaterThanOrEqual, lastFetched.toISOString()),
    includeArchived ? null : equal(archivedColumn, false),
    hasCategory && categoryColumn ? isNotNull(categoryColumn) : null,
  );

export const evidenceLabelsFilter = (
  start: Moment,
  end: Moment,
  lastFetched: Date,
  includeExtinct: boolean,
  includeArchived = false,
) =>
  evidenceContextFilter({
    start,
    end,
    lastFetched,
    includeExtinct,
    updatedAtColumn: VantageNames.TableQueryContextColumns.ContextLabelsUpdatedAt,
    archivedColumn: VantageNames.TableQueryContextColumns.ContextLabelsArchived,
    includeArchived,
  });

export const evidenceNumericsFilter = (
  start: Moment,
  end: Moment,
  lastFetched: Date,
  includeExtinct: boolean,
  includeArchived = false,
) =>
  evidenceContextFilter({
    start,
    end,
    lastFetched,
    includeExtinct,
    updatedAtColumn: VantageNames.TableQueryContextColumns.ContextNumericUpdatedAt,
    archivedColumn: VantageNames.TableQueryContextColumns.ContextNumericArchived,
    includeArchived,
  });

export const evidenceCommentsFilter = (
  start: Moment,
  end: Moment,
  lastFetched: Date,
  includeExtinct: boolean,
  includeArchived = false,
  hasCategory = true,
) =>
  evidenceContextFilter({
    start,
    end,
    lastFetched,
    includeExtinct,
    updatedAtColumn: VantageNames.TableQueryContextColumns.ContextCommentsUpdatedAt,
    archivedColumn: VantageNames.TableQueryContextColumns.ContextCommentsArchived,
    categoryColumn: VantageNames.TableQueryContextColumns.ContextCommentsCategoryId,
    includeArchived,
    hasCategory,
  });

export const evidenceOpaqueFilter = (
  start: Moment,
  end: Moment,
  lastFetched: Date,
  includeExtinct: boolean,
  includeArchived = false,
) =>
  evidenceContextFilter({
    start,
    end,
    lastFetched,
    includeExtinct,
    updatedAtColumn: VantageNames.TableQueryContextColumns.ContextOpaqueUpdatedAt,
    archivedColumn: VantageNames.TableQueryContextColumns.ContextOpaqueArchived,
    includeArchived,
  });

export const evidenceLabels = async (evidenceTableId: string, filter: Filter, cancellationGroup?: string) => {
  const { data } = await getTable(
    evidenceTableId,
    {
      limit: EVIDENCE_CONTEXT_LIMIT,
      columns: [
        SeeqNames.MaterializedTables.ItemIdColumn,
        SeeqNames.MaterializedTables.DatumIdColumn,
        VantageNames.TableQueryContextColumns.ContextLabelsId,
        VantageNames.TableQueryContextColumns.ContextLabelsLabelId,
        VantageNames.TableQueryContextColumns.ContextLabelsLabelName,
        VantageNames.TableQueryContextColumns.ContextLabelsCategoryName,
        VantageNames.TableQueryContextColumns.ContextLabelsCreatedAt,
        VantageNames.TableQueryContextColumns.ContextLabelsUpdatedAt,
        VantageNames.TableQueryContextColumns.ContextLabelsArchived,
        VantageNames.TableQueryContextColumns.ContextLabelsCreatorId,
      ],
      filter,
      properties: GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE_LABELS,
    },
    cancellationGroup ? `${cancellationGroup}-evidenceLabels` : undefined,
  );
  return data;
};

export const evidenceNumerics = async (evidenceTableId: string, filter: Filter, cancellationGroup?: string) => {
  const { data } = await getTable(
    evidenceTableId,
    {
      limit: EVIDENCE_CONTEXT_LIMIT,
      columns: [
        SeeqNames.MaterializedTables.ItemIdColumn,
        SeeqNames.MaterializedTables.DatumIdColumn,
        VantageNames.TableQueryContextColumns.ContextNumericId,
        VantageNames.TableQueryContextColumns.ContextNumericValue,
        VantageNames.TableQueryContextColumns.ContextNumericCategoryId,
        VantageNames.TableQueryContextColumns.ContextNumericCategoryName,
        VantageNames.TableQueryContextColumns.ContextNumericStartTime,
        VantageNames.TableQueryContextColumns.ContextNumericEndTime,
        VantageNames.TableQueryContextColumns.ContextNumericCreatedAt,
        VantageNames.TableQueryContextColumns.ContextNumericUpdatedAt,
        VantageNames.TableQueryContextColumns.ContextNumericArchived,
        VantageNames.TableQueryContextColumns.ContextNumericCreatorId,
      ],
      filter,
      properties: GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE_NUMERICS,
    },
    cancellationGroup ? `${cancellationGroup}-evidenceLabels` : undefined,
  );
  return data;
};

export const evidenceComments = async (evidenceTableId: string, filter: Filter, cancellationGroup?: string) => {
  const { data } = await getTable(
    evidenceTableId,
    {
      limit: EVIDENCE_CONTEXT_LIMIT,
      columns: [
        SeeqNames.MaterializedTables.ItemIdColumn,
        SeeqNames.MaterializedTables.DatumIdColumn,
        VantageNames.TableQueryContextColumns.ContextCommentsId,
        VantageNames.TableQueryContextColumns.ContextCommentsComment,
        VantageNames.TableQueryContextColumns.ContextCommentsCategoryId,
        VantageNames.TableQueryContextColumns.ContextCommentsCategoryName,
        VantageNames.TableQueryContextColumns.ContextCommentsStartTime,
        VantageNames.TableQueryContextColumns.ContextCommentsEndTime,
        VantageNames.TableQueryContextColumns.ContextCommentsCreatedAt,
        VantageNames.TableQueryContextColumns.ContextCommentsUpdatedAt,
        VantageNames.TableQueryContextColumns.ContextCommentsArchived,
        VantageNames.TableQueryContextColumns.ContextCommentsCreatorId,
      ],
      filter,
      properties: GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE_COMMENTS,
    },
    cancellationGroup ? `${cancellationGroup}-evidenceLabels` : undefined,
  );
  return data;
};

export const evidenceOpaque = async (evidenceTableId: string, filter: Filter, cancellationGroup?: string) => {
  const { data } = await getTable(
    evidenceTableId,
    {
      limit: EVIDENCE_CONTEXT_LIMIT,
      columns: [
        SeeqNames.MaterializedTables.ItemIdColumn,
        SeeqNames.MaterializedTables.DatumIdColumn,
        VantageNames.TableQueryContextColumns.ContextOpaqueId,
        VantageNames.TableQueryContextColumns.ContextOpaqueData,
        VantageNames.TableQueryContextColumns.ContextOpaqueKey,
        VantageNames.TableQueryContextColumns.ContextOpaqueStartTime,
        VantageNames.TableQueryContextColumns.ContextOpaqueEndTime,
        VantageNames.TableQueryContextColumns.ContextOpaqueCreatedAt,
        VantageNames.TableQueryContextColumns.ContextOpaqueUpdatedAt,
        VantageNames.TableQueryContextColumns.ContextOpaqueArchived,
        VantageNames.TableQueryContextColumns.ContextOpaqueCreatorId,
      ],
      filter,
      properties: GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE_OPAQUE,
    },
    cancellationGroup ? `${cancellationGroup}-evidenceOpaque` : undefined,
  );
  return data;
};

export async function labelHierarchies(
  categoryLabelPairs: CategoryLabelPairInputV1[],
): Promise<LabelHierarchyOutputV1[]> {
  const input = {
    pairs: categoryLabelPairs,
    depth: 10,
    includeArchived: false,
    maxNodes: 10000,
  };

  const { data } = await sqContextApi.getLabelHierarchies(input);
  return data.hierarchies;
}
